import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, TrendingDown, ShoppingCart, Package } from 'lucide-react';
import { productsApi } from '../../api/endpoints/products';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { CurrencyDisplay, SarIcon } from '../../components/ui/CurrencyDisplay';
import { Badge } from '../../components/ui/Badge';
import { formatRiyadhDate } from '../../lib/formatDate';

const MONTH_NAMES_AR = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

interface ProductStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  productTitle?: string;
}

export function ProductStatsModal({ isOpen, onClose, productId, productTitle }: ProductStatsModalProps) {
  const { storeId } = useParams<{ storeId: string }>();
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  const { data, isLoading } = useQuery({
    queryKey: ['product-stats', storeId, productId],
    queryFn: () => productsApi.stats(storeId!, productId),
    enabled: !!storeId && !!productId && isOpen,
  });

  const chartData = (data?.chart || []).map((d: any) => {
    const [y, m] = d.month.split('-');
    const label = isAr ? MONTH_NAMES_AR[parseInt(m) - 1] : d.month;
    return { ...d, label };
  });

  const growthRate = data?.lastMonth?.sales > 0
    ? (((data.thisMonth.sales - data.lastMonth.sales) / data.lastMonth.sales) * 100).toFixed(1)
    : data?.thisMonth?.sales > 0 ? '100' : '0';
  const isGrowthPositive = Number(growthRate) >= 0;

  const deliveryLabels: Record<string, string> = {
    confirming: t('orders.confirming', 'Confirming'),
    confirmed: t('orders.confirmed', 'Confirmed'),
    delivering: t('orders.delivering', 'Delivering'),
    delivered: t('orders.delivered', 'Delivered'),
    cancelled: t('orders.cancelled', 'Cancelled'),
    returned: t('orders.returned', 'Returned'),
  };

  const deliveryColors: Record<string, string> = {
    confirming: 'warning',
    confirmed: 'info',
    delivering: 'info',
    delivered: 'success',
    cancelled: 'danger',
    returned: 'danger',
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={productTitle || t('products.stats', 'Product Stats')} size="lg">
      {isLoading ? (
        <div className="flex justify-center py-16"><LoadingSpinner /></div>
      ) : !data ? (
        <p className="text-center text-text-muted py-8">{t('common.error')}</p>
      ) : (
        <div className="space-y-6">
          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl border border-border-default bg-bg-tertiary p-4 text-center">
              <p className="text-xs text-text-muted mb-1">{t('products.totalSales', 'Total Sales')}</p>
              <p className="text-2xl font-bold">{data.totalSales.toLocaleString()}</p>
            </div>
            <div className="rounded-xl border border-border-default bg-bg-tertiary p-4 text-center">
              <p className="text-xs text-text-muted mb-1">{t('products.totalRevenue', 'Total Revenue')}</p>
              <p className="text-2xl font-bold"><CurrencyDisplay amount={data.totalRevenue} /></p>
            </div>
            <div className="rounded-xl border border-border-default bg-bg-tertiary p-4 text-center">
              <p className="text-xs text-text-muted mb-1">{t('products.thisMonth', 'This Month')}</p>
              <p className="text-2xl font-bold">{data.thisMonth.sales}</p>
              <p className="text-xs text-text-muted"><CurrencyDisplay amount={data.thisMonth.revenue} /></p>
            </div>
            <div className="rounded-xl border border-border-default bg-bg-tertiary p-4 text-center">
              <p className="text-xs text-text-muted mb-1">{t('products.growth', 'Growth')}</p>
              <div className={`flex items-center justify-center gap-1 text-xl font-bold ${isGrowthPositive ? 'text-success' : 'text-danger'}`}>
                {isGrowthPositive ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
                {growthRate}%
              </div>
              <p className="text-xs text-text-muted">{t('products.vsLastMonth', 'vs last month')}</p>
            </div>
          </div>

          {/* Chart */}
          {chartData.length > 0 && (
            <div className="rounded-xl border border-border-default bg-bg-tertiary p-4">
              <h3 className="text-sm font-semibold mb-3">{t('products.salesOverTime', 'Sales Over Time')}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-default)" />
                  <XAxis dataKey="label" tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }} />
                  <YAxis tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-default)', borderRadius: 8 }}
                    labelStyle={{ color: 'var(--color-text-primary)' }}
                    formatter={(value: number, name: string) => [
                      name === 'revenue' ? `${value.toLocaleString()} SAR` : value,
                      name === 'revenue' ? t('products.revenue', 'Revenue') : t('products.sales', 'Sales')
                    ]}
                  />
                  <Area type="monotone" dataKey="sales" stroke="#2196F3" fill="#2196F3" fillOpacity={0.15} strokeWidth={2} />
                  <Area type="monotone" dataKey="revenue" stroke="#4CAF50" fill="#4CAF50" fillOpacity={0.1} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Recent orders */}
          {data.recentOrders?.length > 0 && (
            <div className="rounded-xl border border-border-default bg-bg-tertiary p-4">
              <h3 className="text-sm font-semibold mb-3">{t('products.recentOrders', 'Recent Orders')}</h3>
              <div className="space-y-2">
                {data.recentOrders.map((order: any) => (
                  <div key={order._id} className="flex items-center justify-between gap-3 rounded-lg bg-bg-secondary px-3 py-2.5">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bg-tertiary">
                        <ShoppingCart className="h-3.5 w-3.5 text-text-muted" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {order.customer || t('orders.anonymous', 'Anonymous')}
                          {order.orderNumber && <span className="text-text-muted ms-1">#{order.orderNumber}</span>}
                        </p>
                        <p className="text-xs text-text-muted">
                          {formatRiyadhDate(order.date, { month: 'short', day: 'numeric', year: undefined }, isAr ? 'ar-SA' : 'en-US')}
                          {order.quantity > 1 && ` · ${order.quantity}x`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant={deliveryColors[order.delivery] as any || 'default'} className="text-[10px]">
                        {deliveryLabels[order.delivery] || order.delivery}
                      </Badge>
                      <span className="text-sm font-semibold">
                        <CurrencyDisplay amount={order.amount} />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {data.totalSales === 0 && (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-text-muted/30 mx-auto mb-3" />
              <p className="text-text-muted">{t('products.noSalesYet', 'No sales yet for this product')}</p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
